import { staleTimes, useMutation, useQuery } from '@platform/api-client';
import type { Operation, RequestBody, ResponseBody, TypedClient } from '@platform/openapi';
import { z } from 'zod';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type Member = ResponseBody<Operation<'/admin/team', 'get'>>['items'][number];
export type AccessPreset = NonNullable<Member['preset']>;
export type Permission = Member['permissions'][number];
export type InvitationBody = RequestBody<Operation<'/admin/team/invitations', 'post'>>;
export type AccessBody = RequestBody<Operation<'/admin/team/{id}/access', 'put'>>;

export const USERS_MANAGE_ROLE = 'users:manage';
export const ADMIN_ROLE = 'merchant-admin';

export const ACCESS_PRESETS: readonly AccessPreset[] = ['staff', 'viewer', 'admin'];

export const PERMISSION_GROUPS: readonly {
  readonly id: string;
  readonly permissions: readonly Permission[];
}[] = [
  {
    id: 'orders',
    permissions: ['orders:write', 'orders:confirm', 'orders:dispatch', 'orders:pii:read', 'orders:refund'],
  },
  { id: 'catalog', permissions: ['catalog:write', 'catalog:cost'] },
  { id: 'stock', permissions: ['inventory:write'] },
  { id: 'finance', permissions: ['finance:read', 'finance:write'] },
  { id: 'settings', permissions: ['carriers:manage', 'settings:manage', 'users:manage', 'audit:read'] },
];

const ADMIN_ONLY: readonly Permission[] = ['users:manage'];

export const permissionKeyOf = (permission: Permission): string =>
  `team.perm_${permission.replaceAll(':', '_')}`;

export const grantablePermissions = (isAdmin: boolean): readonly Permission[] =>
  PERMISSION_GROUPS.flatMap((group) => group.permissions).filter(
    (permission) => isAdmin || !ADMIN_ONLY.includes(permission),
  );

export const grantablePresets = (isAdmin: boolean): readonly AccessPreset[] =>
  ACCESS_PRESETS.filter((preset) => isAdmin || preset !== 'admin');

export type MemberStatus = 'active' | 'pending' | 'paused';

export const statusOf = (member: Member): MemberStatus => {
  if (!member.active) return 'paused';
  return member.invitationPending ? 'pending' : 'active';
};

export const displayNameOf = (member: Pick<Member, 'firstName' | 'lastName' | 'email'>): string =>
  [member.firstName, member.lastName].filter((part) => part.trim() !== '').join(' ') || member.email;

export const accessFormSchema = z.object({
  preset: z.enum(['admin', 'staff', 'viewer']),
  permissions: z.array(z.string()),
  subsidiaryId: z.string(),
});

export const invitationFormSchema = accessFormSchema.extend({
  email: z.string().trim().pipe(z.email()),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().max(100),
});

export type AccessFormValues = z.infer<typeof accessFormSchema>;
export type InvitationFormValues = z.infer<typeof invitationFormSchema>;

const isPermission = (value: string): value is Permission =>
  PERMISSION_GROUPS.some((group) => (group.permissions as readonly string[]).includes(value));

export const accessBodyOf = (values: AccessFormValues): AccessBody => ({
  preset: values.preset,
  permissions: values.permissions.filter(isPermission),
  subsidiaryId: values.subsidiaryId === '' ? null : values.subsidiaryId,
});

export const invitationBodyOf = (values: InvitationFormValues): InvitationBody => ({
  ...accessBodyOf(values),
  email: values.email,
  firstName: values.firstName,
  lastName: values.lastName,
});

export const accessFormOf = (member: Member | null): AccessFormValues => ({
  preset: member?.preset ?? 'staff',
  permissions: [...(member?.permissions ?? [])],
  subsidiaryId: member?.subsidiaryId ?? '',
});

export const useTeam = (api: TypedClient) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.team(tenantId),
    queryFn: () => api.get('/admin/team', {}),
    staleTime: staleTimes.settings,
    select: (data) => data.items,
  });
};

export type TeamAction =
  | { readonly kind: 'invite'; readonly body: InvitationBody }
  | { readonly kind: 'access'; readonly member: Member; readonly body: AccessBody }
  | { readonly kind: 'status'; readonly member: Member; readonly active: boolean }
  | { readonly kind: 'remove'; readonly member: Member }
  | { readonly kind: 'resend'; readonly member: Member };

export const useTeamActions = (api: TypedClient) => {
  const { invalidate } = usePageContext();
  const send = async (
    action: TeamAction,
  ): Promise<{ readonly invitationSent: boolean; readonly member: Member | null }> => {
    if (action.kind === 'invite') return api.post('/admin/team/invitations', { body: action.body });
    const path = { id: action.member.userId };
    if (action.kind === 'access')
      return {
        invitationSent: false,
        member: await api.put('/admin/team/{id}/access', { path, body: action.body }),
      };
    if (action.kind === 'status')
      return {
        invitationSent: false,
        member: await api.put('/admin/team/{id}/status', { path, body: { active: action.active } }),
      };
    if (action.kind === 'remove') await api.delete('/admin/team/{id}', { path });
    else await api.post('/admin/team/invitations/resend', { body: { userId: action.member.userId } });
    return { invitationSent: action.kind === 'resend', member: null };
  };
  return useMutation({ mutationFn: send, onSuccess: () => invalidate('team') });
};
